import React, { useEffect, useContext, useState } from 'react';
import { ChartContainer, TableHeading } from './RegionVolumeDetailsTable.style';
import { getSnapshotRegionVolumeDetailsTableData } from '../../../../../../api/services';
import { BaseTable, Flexbox } from '@sede-enterprise/shell-ds-react-framework';
import { AppContext } from '../../../../../../Context/AppContext';
import Loader from '../../../../../Loader/Loader';
import { RegionVolumeDetailsTableData } from '../../../../../../Types/types';
import DownloadCsvButton from '../../../../../ReUsableComponents/DownloadCsvButton/DownloadCsvButton';
import { SortingState, getPaginationRowModel, getSortedRowModel } from '@tanstack/react-table';
import {
  OUT_OF_SCOPE_VOLUME_PARAMETER_NAME,
  WHOLESALE_CHURN_PARAMETER_NAME,
} from '../../SnapshotWaterfallChart/SnapshotWaterfallChartUtils';
import { NCF_DASHBOARD_QTY_DECIMAL_PLACES } from '../../../../../../api/constants';
import { notify } from '../../../../../../../CarbonDashboard/components/Toast/notify';
import { errorNotifyMessage } from '../../../../../Utils/utils';

const RegionVolumeDetailsTable: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const { state: analyticsState, dispatch: snapshotDispatch } =
    useContext(AppContext).analyticsSnapshotReport;

  const mapData: RegionVolumeDetailsTableData[] = analyticsState.volumeByRegionDetailsTableData;
  const sum = mapData
    ?.reduce((acc: number, item: RegionVolumeDetailsTableData) => acc + item.QTY, 0)
    ?.toFixed(NCF_DASHBOARD_QTY_DECIMAL_PLACES);
  const tableFormattedData = [...mapData].map((el) => {
    return {
      ...el,
      QTY: Math.round(Number(el.QTY) * 1000) / 1000, // Round to 3 decimal places
    };
  });

  const columnNames = [
    'YEAR',
    'QUARTER',
    'MONTH',
    'COMMODITY',
    'TRANSACTION_TYPE',
    'ORIGINAL_TRANSACTION_TYPE',
    'NCF_CATEGORY',
    'CPTY',
    'CPTY_DESC',
    'QTY',
  ];
  const columns = columnNames.map((key) => ({
    header: key.replace(/_/g, ' '),
    accessorKey: key,
  }));

  useEffect(() => {
    snapshotDispatch({
      type: 'setRegionVolumeDetailsTableData',
      payload: [],
    });
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const parameterName =
          analyticsState.detailedViewParameter.parameterName === WHOLESALE_CHURN_PARAMETER_NAME
            ? OUT_OF_SCOPE_VOLUME_PARAMETER_NAME
            : analyticsState.detailedViewParameter.parameterName;
        const region = [analyticsState.detailedViewParameter.region];
        if (region && parameterName) {
          const response = await getSnapshotRegionVolumeDetailsTableData({
            ...analyticsState.analyticsSnapshotReportFilteredValues,
            parameterName,
            region,
          });
          // Use the snapshotDispatch function to call setNcfQuarterwise
          snapshotDispatch({
            type: 'setRegionVolumeDetailsTableData',
            payload: response.data,
          });
        }
      } catch (error) {
        notify('error', errorNotifyMessage(error));
        snapshotDispatch({
          type: 'setError',
          payload: {
            isError: true,
            message: 'Failed to fetch data. Please try again.',
          },
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [
    snapshotDispatch,
    analyticsState.analyticsSnapshotReportFilteredValues,
    analyticsState.detailedViewParameter.parameterName,
    analyticsState.detailedViewParameter.region,
  ]); // Add snapshotDispatch to the dependency array

   const tableOptions = {
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  };

  return (
    <ChartContainer>
      <Loader loader={isLoading} message={''} />
      <Flexbox style={{ width: '100%' }} justifyContent='space-between'>
        <TableHeading>
          Detail View of {analyticsState?.detailedViewParameter?.parameterName}
        </TableHeading>
        <DownloadCsvButton data={tableFormattedData} fileName={'RegionVolume'} />
      </Flexbox>
      <BaseTable
        columns={columns}
        data={tableFormattedData}
        size='large'
        data-testid='base-table'
        style={{ width: '100%' }}
        stickyHeader={true}
        tableOptions={tableOptions as unknown as Object}
      />
      {Math.abs(Number(sum)) > 0 && (
        <Flexbox justifyContent='flex-start' style={{ marginTop: '10px' }}>
          <TableHeading>Total: {sum}</TableHeading>
        </Flexbox>
      )}
    </ChartContainer>
  );
};

export default RegionVolumeDetailsTable;
